import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../store/auth";
import api from "../api/client";
import Icon from "../components/Icon";
import { applyAppTimezone } from "../utils";
import { useT } from "../i18n";

const FEATURES = [
  ["zap", "Tiempo real", "Cambios y notificaciones al instante"],
  ["chart", "Reportes ejecutivos", "KPIs, cumplimiento y agilidad"],
  ["grid", "Kanban · Gantt · Métricas", "Tu trabajo, como prefieras verlo"],
];

export default function Login() {
  const { login, user } = useAuth();
  const t = useT();
  const nav = useNavigate();
  const [email, setEmail] = useState("admin@redesip.org");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [maint, setMaint] = useState(null);

  useEffect(() => {
    api.get("/auth/maintenance").then((r) => { setMaint(r.data); if (r.data?.app_tz) applyAppTimezone(r.data.app_tz); }).catch(() => {});
  }, []);

  if (user) nav("/", { replace: true });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const u = await login(email.trim(), password);
      let home = "/";
      try { home = (u?.ui_prefs ? JSON.parse(u.ui_prefs).home_page : "") || "/"; } catch { home = "/"; }
      nav(home, { replace: true });
    } catch (err) {
      setError(err.response?.data?.detail || "No se pudo iniciar sesión");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen">
      {/* Brand panel */}
      <div className="relative hidden w-1/2 flex-col justify-between overflow-hidden bg-gradient-to-br from-brand-600 via-brand-700 to-brand-900 p-12 text-white lg:flex">
        <div className="pointer-events-none absolute -right-24 -top-24 h-80 w-80 rounded-full bg-white/10 blur-2xl" />
        <div className="pointer-events-none absolute -bottom-32 -left-16 h-96 w-96 rounded-full bg-brand-400/20 blur-3xl" />
        <div className="relative flex items-center gap-3">
          <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-white/15 text-base font-extrabold backdrop-blur">G<span style={{ color: "#6ab234" }}>+</span></span>
          <span className="text-xl font-extrabold tracking-tight">Gestion<span style={{ color: "#6ab234" }}>IT</span></span>
        </div>
        <div className="relative">
          <h1 className="max-w-sm text-4xl font-extrabold leading-tight text-white">
            La gestión de tu equipo de TI, bajo control.
          </h1>
          <p className="mt-3 max-w-sm text-brand-100">
            Grupo David · Dirección de Tecnología. Tareas, seguimiento y reportes en un solo lugar.
          </p>
          <div className="mt-8 space-y-3">
            {FEATURES.map(([icon, t, d]) => (
              <div key={t} className="flex items-start gap-3">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-white/15 backdrop-blur"><Icon name={icon} size={18} /></span>
                <div>
                  <div className="font-semibold">{t}</div>
                  <div className="text-sm text-brand-100">{d}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="relative text-xs text-brand-200">© {new Date().getFullYear()} GestionIT · git.grupodavid1.com</div>
      </div>

      {/* Form panel */}
      <div className="flex w-full items-center justify-center bg-slate-50 p-6 lg:w-1/2">
        <div className="w-full max-w-sm">
          <div className="mb-8 flex items-center gap-2 lg:hidden">
            <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-brand-600 text-sm font-extrabold text-white">G<span style={{ color: "#6ab234" }}>+</span></span>
            <span className="text-lg font-extrabold">Gestion<span style={{ color: "#6ab234" }}>IT</span></span>
          </div>

          <h2 className="text-2xl font-extrabold text-slate-900">{t("login.welcome")}</h2>
          <p className="mb-7 mt-1 text-sm text-slate-500">{t("login.subtitle")}</p>

          {maint?.enabled && (
            <div className="mb-5 flex items-start gap-2.5 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2.5 text-sm text-amber-800">
              <Icon name="shield" size={17} className="mt-0.5 shrink-0 text-amber-600" />
              <div>
                <div className="font-bold">Sistema en mantenimiento</div>
                <div className="text-[13px]">{maint.message || "Solo el personal administrador puede ingresar en este momento."}</div>
              </div>
            </div>
          )}

          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="label">Email</label>
              <input className="input" value={email} onChange={(e) => setEmail(e.target.value)} type="email" autoFocus placeholder="tu.correo@redesip.org" />
            </div>
            <div>
              <div className="flex items-center justify-between">
                <label className="label mb-0">{t("login.password")}</label>
                <Link to="/forgot-password" className="mb-1 text-xs font-medium text-brand-600 hover:underline">{t("login.forgot")}</Link>
              </div>
              <input className="input" value={password} onChange={(e) => setPassword(e.target.value)} type="password" placeholder="••••••••" />
            </div>
            {error && (
              <div className="flex items-center gap-2 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
                <Icon name="alert" size={15} />{error}
              </div>
            )}
            <button className="btn-primary h-11 w-full justify-center text-[15px]" disabled={busy}>
              {busy ? t("login.signingin") : t("login.signin")}
            </button>
          </form>

          <p className="mt-6 text-center text-xs text-slate-400">
            {t("login.help")}
          </p>
        </div>
      </div>
    </div>
  );
}
